import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import DataTable from '../../components/ui/data-table';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { Calendar, Plus, Edit, Trash2, MapPin, Clock } from 'lucide-react';

const EventsPage = () => {
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      // TODO: Replace with actual API call
      // const res = await staffAPI.getEvents();
      // setEvents(res.data.data || []);
      
      // Placeholder data for now
      setEvents([]);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchEvents();
  }, []);

  const columns = [
    { id: 'title', header: 'Event Title', accessorKey: 'title', sortable: true },
    { id: 'date', header: 'Date', accessorKey: 'date', sortable: true },
    { id: 'time', header: 'Time', accessorKey: 'time' },
    { id: 'location', header: 'Location', accessorKey: 'location' },
    { id: 'type', header: 'Type', accessorKey: 'type', sortable: true },
    { id: 'status', header: 'Status', accessorKey: 'status', sortable: true },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline">
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  const eventTypes = [
    { id: 'academic', name: 'Academic Events', color: 'bg-blue-100 text-blue-800' },
    { id: 'sports', name: 'Sports Events', color: 'bg-green-100 text-green-800' },
    { id: 'cultural', name: 'Cultural Events', color: 'bg-purple-100 text-purple-800' },
    { id: 'meetings', name: 'Meetings', color: 'bg-yellow-100 text-yellow-800' }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchEvents}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Event Management</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            Calendar View
          </Button>
          <Button size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>
        </div>
      </div>

      {/* Event Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {eventTypes.map((type) => (
          <Card key={type.id}>
            <CardContent className="p-4">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-gray-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">{type.name}</p>
                  <p className="text-2xl font-bold text-gray-900">0</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upcoming Events */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Events</CardTitle>
        </CardHeader>
        <CardContent>
          {(!events || events.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No upcoming events</p>
              <p className="text-sm">Create your first event to get started</p>
              <Button className="mt-4" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((event) => (
                <div key={event.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <Calendar className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">{event.title}</h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {event.time}
                        </span>
                        <span className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {event.location}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${eventTypes.find(t => t.id === event.type)?.color || 'bg-gray-100 text-gray-800'}`}>
                      {event.type}
                    </span>
                    <Button size="sm" variant="outline">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Events Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Events</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable data={events} columns={columns} searchable />
        </CardContent>
      </Card>
    </div>
  );
};

export default EventsPage;
